// utils/subscribeHelpers.ts
import { nextTick } from 'vue'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useAuditNonconformityFormStore } from '@/modules/audits/stores/useAuditNonconformityFormStore/useAuditNonconformityFormStore'
import { useAuditNonconfrormitiesStore } from '@/modules/audits/stores/useAuditNonconfrormitiesStore/useAuditNonconfrormitiesStore'
import { useAuditRegisterStore } from '@/modules/audits/stores/useAuditRegisterStore/useAuditRegisterStore'
import { useCentrifugeStore } from '@/modules/centrifuge/stores/centrifugeStore'
import type { IRealtimePayload } from '@/modules/centrifuge/types/IRealtimePayload'
import { useNotificationSound } from '@/modules/notifications/composables/useNotificationSound'
import { useNotificationStore } from '@/modules/notifications/stores/notificationStore'
import type { INotificationDto } from '@/modules/notifications/types/notification.types'
import { useTicketsStore } from '@/modules/tickets/stores/ticketsStore'
import { useImprovementsStore } from '@/modules/improvements/stores/improvementsStore'
import { useImportStore } from '@/modules/import/stores/importStore'
import type { IImportCompletedEvent, IImportProgressEvent } from '@/modules/import/types/IImportActive'
import { useTasksStore } from '@/modules/tasks/stores/tasksStore'
import { useActionRequiredCountersStore } from '@/modules/notifications/stores/actionRequiredCountersStore'
import { useUserStore } from '@/modules/user/stores/userStore'

/**
 * Возвращает имя единого канала пользователя для текущей сессии.
 * Канал имеет вид `user:user#sub`, где sub — поле из IAM (Logto-идентификатор).
 * Если sub ещё не загружен — возвращает null, подписка не поднимается.
 */
const buildUserChannel = (): string | null => {
  const userStore = useUserStore()
  const sub = userStore.user?.sub
  if (!sub) return null
  return `user:user#${sub}`
}

export const useCentrifugeHelper = () => {
  const centrifugeStore = useCentrifugeStore()

  /**
   * Инициализирует Centrifuge соединение если оно еще не установлено.
   */
  const ensureConnection = async (): Promise<void> => {
    if (!centrifugeStore.connected) {
      await centrifugeStore.initCentrifuge()
    }
  }

  /**
   * Общий диспетчер входящих payload по единому каналу user:user#sub.
   *
   * Маршрутизация по event_type:
   * - adverse_event_* / voice_template → apiStore.applyAdverseEventRealtime
   * - ticket_*                         → ticketsStore.applyTicketRealtime
   * - audit_*                          → auditRegisterStore.applyAuditRealtime
   * - responsibility_entry_*           → tasksStore.applyResponsibilityEntryRealtime
   * - new_notification / notification_updated → notificationStore.handleRealtimeEvent
   *                                             (+ useNotificationSound().play() для new_notification)
   * - import_completed                 → importStore.applyImportCompleted
   * Неизвестные события игнорируются; сообщения чатов поступают через Matrix SDK.
   */
  const dispatchRealtime = async (data: IRealtimePayload): Promise<void> => {
    if (!data?.event_type) return

    const eventType = data.event_type as string

    if (eventType.startsWith('adverse_event_') || eventType === 'voice_template') {
      const apiStore = useApiStore()
      await apiStore.applyAdverseEventRealtime(data)
      return
    }

    if (eventType.startsWith('ticket_')) {
      const ticketsStore = useTicketsStore()
      await ticketsStore.applyTicketRealtime(data)
      return
    }

    if (eventType.startsWith('improvement_suggestion_')) {
      const improvementsStore = useImprovementsStore()
      await improvementsStore.applyImprovementRealtime(data)
      return
    }

    if (eventType.startsWith('auditor_report_')) {
      const auditNonconformitiesStore = useAuditNonconfrormitiesStore()
      await auditNonconformitiesStore.applyAuditReportRealtime(data)
      return
    }

    if (eventType.startsWith('audit_')) {
      const auditRegisterStore = useAuditRegisterStore()
      await auditRegisterStore.applyAuditRealtime(data)
      return
    }

    if (eventType.startsWith('responsibility_entry_')) {
      const tasksStore = useTasksStore()
      await tasksStore.applyResponsibilityEntryRealtime(data)

      const entry = data.data as any
      const sourceType = entry?.source_type

      if (sourceType === 'adverseevent') {
        const apiStore = useApiStore()
        await apiStore.applyResponsibilityEntryRealtime(data)
      } else if (sourceType === 'ticket') {
        const ticketsStore = useTicketsStore()
        await ticketsStore.applyResponsibilityEntryRealtime(data)
      } else if (sourceType === 'improvementsuggestion') {
        const improvementsStore = useImprovementsStore()
        await improvementsStore.applyResponsibilityEntryRealtime(data)
      } else if (sourceType === 'auditorreport' || sourceType === 'correctiveactionreport') {
        const auditNonconformityFormStore = useAuditNonconformityFormStore()
        await auditNonconformityFormStore.applyResponsibilityEntryRealtime(data)
      }
      return
    }

    if (eventType === 'equipment_changed') {
      window.dispatchEvent(new CustomEvent('equipment-remote-change', { detail: data.data }))
      return
    }

    if (eventType === 'import_progress') {
      const importStore = useImportStore()
      importStore.applyProgressRealtime(data.data as IImportProgressEvent)
      return
    }

    if (eventType === 'import_completed') {
      const importStore = useImportStore()
      importStore.applyImportCompleted(data.data as IImportCompletedEvent)
      return
    }

    if (eventType === 'action_required_counters_updated') {
      const countersStore = useActionRequiredCountersStore()
      countersStore.setCounters(data.data as any)

      const item = (data.data as any)?.item
      if (item && item.id != null && typeof item.requires_action === 'boolean') {
        const id = Number(item.id)
        const reqAction = item.requires_action
        switch (item.module) {
          case 'tasks': {
            const tasksStore = useTasksStore()
            tasksStore.updateRequiresAction(id, reqAction)
            break
          }
          case 'tickets': {
            const ticketsStore = useTicketsStore()
            ticketsStore.updateRequiresAction(id, reqAction)
            break
          }
          case 'adverse_events':
          case 'adverse': {
            const apiStore = useApiStore()
            apiStore.updateRequiresAction(id, reqAction)
            break
          }
          case 'improvements': {
            const improvementsStore = useImprovementsStore()
            improvementsStore.updateRequiresAction(id, reqAction)
            break
          }
          case 'audits': {
            const auditRegisterStore = useAuditRegisterStore()
            auditRegisterStore.updateRequiresAction(id, reqAction)
            break
          }
        }
      }
      return
    }

    if (eventType === 'new_notification' || eventType === 'notification_updated') {
      const notificationStore = useNotificationStore()
      notificationStore.handleRealtimeEvent(data)
      if (eventType === 'new_notification') {
        const dto = data.data as INotificationDto | undefined
        if (dto) useNotificationSound().play(dto)
      }
    }
  }

  /**
   * Поднимает единую подписку на канал user:user#sub с общим диспетчером.
   * Идемпотентно: повторный вызов на тот же канал не создаёт дубля
   * (защита внутри centrifugeStore.subscribe).
   */
  const subscribeToUserChannel = async (): Promise<void> => {
    try {
      await ensureConnection()

      const channel = buildUserChannel()
      if (!channel) {
        centrifugeStore.logEvent('Subscribe skipped: missing user.sub', {
          user: useUserStore().user,
        })
        return
      }

      await nextTick()

      await centrifugeStore.subscribe(
        channel,
        async (data: IRealtimePayload) => {
          await dispatchRealtime(data)
        },
        async () => {
          const imports = useImportStore()
          console.warn(`🔄 [CF Recovery Handler] Сработал recovery для канала ${channel}! Проверяем уведомления, счётчики и импорт (activeImportType: ${imports.activeImportType || 'нет'})`)
          const jobs: Promise<unknown>[] = [
            useNotificationStore().fetchNotifications(),
            useActionRequiredCountersStore().fetchCounters(),
          ]
          if (imports.activeImportType) {
            console.log(`📦 [CF Recovery Handler] Запрашиваем fetchActiveImport и fetchUnfinishedImports для типа "${imports.activeImportType}"...`)
            jobs.push(imports.fetchActiveImport(imports.activeImportType, true))
            jobs.push(imports.fetchUnfinishedImports(imports.activeImportType, true))
          }
          const results = await Promise.allSettled(jobs)
          for (const result of results) {
            if (result.status === 'rejected') {
              console.error('❌ [CF Recovery Handler] Ошибка при выполнении задачи recovery:', result.reason)
              throw result.reason
            }
          }
          console.log('✅ [CF Recovery Handler] Задачи recovery успешно завершены')
        },
      )
    } catch (error) {
      centrifugeStore.logEvent('Subscribe error', { error })
      throw error
    }
  }

  /**
   * Отписка от единого канала пользователя. Вызывается на logout,
   * чтобы при повторном логине не было дубля подписки с прежним токеном.
   */
  const unsubscribeFromUserChannel = (): void => {
    const channel = buildUserChannel()
    if (!channel) return
    centrifugeStore.centrifugeUnsubscribe(channel)
  }

  return {
    ensureConnection,
    subscribeToUserChannel,
    unsubscribeFromUserChannel,
  }
}
